import { STATUS_LABELS } from './mockData'

export const statusLabel = (s) => STATUS_LABELS[s] || s

// Format a UUID into the carrier-facing reference number. Mirrors the backend
// stamp used in outbound RFQ subjects (shipmentService.js:
// `MARTEU-REQ-${quoteRequest.id.replace(/-/g,'').slice(0,12).toUpperCase()}`)
// and the inbound poller's matcher token. IMPORTANT: pass the QuoteRequest UUID,
// not the shipment UUID — they're different rows. shipmentAdapter handles this
// and exposes `shipment.trackingId` on the adapted object. Returns null for
// drafts or anything that doesn't look like a UUID.
export function deriveTrackingId(uuid) {
  if (!uuid || typeof uuid !== 'string') return null
  const compact = uuid.replace(/-/g, '')
  if (compact.length < 12 || !/^[0-9a-f]+$/i.test(compact)) return null
  return `MARTEU-REQ-${compact.slice(0, 12).toUpperCase()}`
}

// Computes the right pill text for a shipment. Most labels are static; PARTIAL
// is dynamic, formatted as "{received} of {total}" from the providers list.
export function getStatusLabel(shipment) {
  if (shipment.status === 'PARTIAL') {
    const provs = shipment.providers || []
    const total = provs.length
    const responded = provs.filter(p =>
      p.status === 'received' || p.status === 'declined'
    ).length
    return `${responded} of ${total}`
  }
  return STATUS_LABELS[shipment.status] || shipment.status
}

/* ====================================================================
   Shipment naming — short, scannable identifiers for the sidebar.
   Format: {Commodity} · {Origin} once commodity is captured, else
   {Origin} → {Destination}. Origin/dest abbreviated heavily (state
   codes for domestic addresses, city names for ports if short, country
   codes if names are long). User-set names always win.
   ==================================================================== */

// US state postal code matcher: looks for ", CT" or ", CT 06470" tail
const STATE_REGEX = /,?\s+([A-Z]{2})(?:\s+\d{5}(?:-\d{4})?)?\s*$/

export function formatLocation(loc, mode) {
  if (!loc) return '—'
  const code = loc.code || ''
  const name = loc.name || ''

  // International freight: prefer the city name if reasonable, else country code
  if (mode && (mode.includes('Ocean') || mode === 'Air')) {
    if (name && name.length <= 10 && !/\d/.test(name) && !name.includes(',')) {
      return name // "Shanghai", "Delhi", "Houston"
    }
    if (loc.country && loc.country.length <= 3) return loc.country // "CN", "US"
    if (code && code.length <= 5) return code // "CNSHA"
    return name.slice(0, 8)
  }

  // Domestic / trucking / mixed: try state extraction from address-style names
  const stateMatch = name.match(STATE_REGEX)
  if (stateMatch) return stateMatch[1] // "CT", "KS"
  // Short codes (NYC, ORD, DFW, MIA, JFK)
  if (code && code.length <= 4) return code
  // Short city names
  if (name && name.length <= 10 && !name.includes(',')) return name
  // Last-ditch: take the bit before the first comma, truncated
  return name.split(',')[0].slice(0, 8) || code || '—'
}

export function autoName(shipment) {
  const isDraft = shipment.isDraft === true
  const commodity = shipment.commodity?.description?.trim()
  const origin = formatLocation(shipment.route?.origin, shipment.mode)
  const dest = formatLocation(shipment.route?.destination, shipment.mode)

  // Empty fresh draft
  if (isDraft && !commodity && (!origin || origin === '—')) return 'New shipment'

  // Commodity captured → "{Commodity} · {Origin}"
  if (commodity) {
    const cap = commodity.length > 14 ? commodity.slice(0, 13).trim() + '…' : commodity
    return `${cap} · ${origin}`
  }

  // No commodity yet → route format
  return `${origin} → ${dest}`
}

export function displayName(shipment, allShipments) {
  if (shipment.userName) return shipment.userName
  const base = autoName(shipment)
  // Collision suffix: append #2, #3 etc if another shipment auto-resolves to the
  // same name. User-named shipments don't participate in collision detection.
  if (!allShipments) return base
  const sameNameShipments = allShipments
    .filter(s => !s.userName && autoName(s) === base)
  if (sameNameShipments.length <= 1) return base
  const idx = sameNameShipments.findIndex(s => s.id === shipment.id) + 1
  return idx > 1 ? `${base} #${idx}` : base
}
